import { groupBy, sortBy, uniq, uniqBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { exists, filterMatches, sortObjectives } from "@examspark/common";
import {
  NoResults,
  useQueriesGetObjectives,
  useSerializedQueryParam,
} from "@examspark/common-app";
import {
  Loading,
  SelectInput,
  TextInput,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import { IconInformationCircleLine } from "@examspark/common-ui/dist/components/icons";
import {
  JumpBar,
  JumpBarContext,
} from "@examspark/common-ui/dist/components/utils/JumpBar";
import { useDebouncedState } from "@examspark/common-ui/dist/hooks/useDebounce";
import { ObjectiveCategoryBadge } from "@examspark/customer/tcmpc";
import {
  Objective,
  ObjectiveConceptMetadata,
  ParsedObjectivesView,
} from "@examspark/types";

import ContentSection, {
  ContentSectionSecondary,
} from "../../components/common/ContentSection";
import { Annotations } from "../../components/unit/Annotations";
import { useQuerySearchBonfireObjectives } from "../../hooks/useQueries";
import ObjectiveDetailModal from "../ObjectiveDetailModal";

import AlignmentViewObjective from "./AlignmentViewObjective";
import { CheckboxGridFilterButton } from "./CheckboxGridFilterButton";

type Props = {
  data: Extract<ParsedObjectivesView, { type: "CONCEPTUAL_ALIGNMENT" }>;
};

type UnselectedMetadata = {
  fullMetadata: ObjectiveConceptMetadata;
  parts?: Array<{
    key: keyof ObjectiveConceptMetadata;
    value: string;
  }>;
  title?: string;
};

type ObjectiveWithMetadata = {
  objective: Objective;
  metadata: UnselectedMetadata;
};

const StyledStickyToolbar = styled(VSpace)`
  position: sticky;
  left: 0;
  top: 0;
  background-color: white;

  padding: 1rem 0rem;

  max-width: calc(100dvw - 4rem);
`;

const StyledConceptSelection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, calc(33% - (2rem / 3)));
  gap: 1rem;
  width: 100%;
`;

const StyledJumpBar = styled(JumpBar)`
  flex-grow: 1;
`;

const StyledIndented = styled.div`
  margin-left: 1rem;
`;

const StyledAlignmentViewObjective = styled(AlignmentViewObjective)`
  margin-bottom: 1rem;
  > ${VSpace} {
    flex-direction: row;
    align-items: flex-start;

    > div:first-child {
      min-width: 7rem;
    }

    > div:nth-child(2) {
      padding-top: 0.5rem;
    }
  }
`;

const StyledFilterTextInput = styled(TextInput)`
  flex-basis: 30rem;
  min-width: 10rem;

  input {
    width: 100%;
  }
`;

const StyledButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Styled = styled.div`
  ${ObjectiveCategoryBadge} {
    float: left;
  }
`;

const EmptySection = styled.div`
  margin: 1rem 0rem;
  font-style: italic;
`;

const ConceptualAlignment = ({ data }: Props) => {
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [jumpBarEntries, setJumpBarEntries] = useState({});
  const [showObjectiveIndex, setShowObjectiveIndex] = useState<number>();
  const [filterText, debouncedFilterText, setFilterText] =
    useDebouncedState("");
  const [selections, setSelections] = useSerializedQueryParam<{
    understanding?: string | undefined;
    concept?: string | undefined;
    unitConcept?: string | undefined;
  }>("selections", {});

  const { understanding, concept, unitConcept } = selections;

  const objectiveMatchesFilter = useCallback(
    (om: ObjectiveWithMetadata) => {
      const metadataParts = om.metadata.parts?.map((p) => p.value) ?? [];
      return filterMatches(
        debouncedFilterText,
        om.objective.content,
        om.objective.abbreviation,
        ...metadataParts,
      );
    },
    [debouncedFilterText],
  );

  const metadataMatchesSelection = useCallback(
    (metadata: ObjectiveConceptMetadata | undefined) =>
      (metadata?.overarchingUnderstanding === understanding ||
        understanding == null) &&
      (metadata?.overarchingConcept === concept || concept == null) &&
      (metadata?.unitConcept === unitConcept || unitConcept == null),
    [understanding, concept, unitConcept],
  );

  const getUnselectedMetadata = useCallback(
    (metadata: ObjectiveConceptMetadata): UnselectedMetadata => {
      const parts: Array<{
        key: keyof ObjectiveConceptMetadata;
        value: string;
      }> = [];
      if (understanding == null) {
        parts.push({
          key: "overarchingUnderstanding",
          value: metadata.overarchingUnderstanding,
        });
      }
      if (concept == null) {
        parts.push({
          key: "overarchingConcept",
          value: metadata.overarchingConcept,
        });
      }
      if (unitConcept == null) {
        parts.push({
          key: "unitConcept",
          value: metadata.unitConcept,
        });
      }

      if (parts.length === 0) {
        return { fullMetadata: metadata };
      }

      return {
        fullMetadata: metadata,
        parts,
        title: parts.map((p) => p.value).join(" - "),
      };
    },
    [concept, understanding, unitConcept],
  );

  const objectivesQuery = useQuerySearchBonfireObjectives(data.objectiveScope);

  const objectivesWithMetadata = useMemo(
    () =>
      objectivesQuery.data
        ?.map((o) => {
          const metadata = data.data?.objectiveMetadata[o.id];
          if (metadata == null) return null;
          if (!metadataMatchesSelection(metadata)) return null;

          return {
            objective: o,
            metadata: getUnselectedMetadata(metadata),
          };
        })
        .filter(exists)
        .filter(objectiveMatchesFilter),
    [
      data.data?.objectiveMetadata,
      getUnselectedMetadata,
      metadataMatchesSelection,
      objectiveMatchesFilter,
      objectivesQuery.data,
    ],
  );

  const scopesQueries = useQueriesGetObjectives(
    useMemo(
      () =>
        uniqBy(
          objectivesQuery.data?.map((o) => ({
            id: o.authorityId ?? undefined,
            sk: `section#${o.scope}`,
          })),
          (key) => key.sk,
        ),
      [objectivesQuery.data],
    ),
  );

  const allQueriesCompleted = !scopesQueries.find((q) => q.isLoading);

  const scopes = useMemo(
    () =>
      allQueriesCompleted
        ? sortObjectives(scopesQueries.flatMap((q) => q.data ?? []))
        : [],
    // can't depend on scopesQueries because it doesn't memoize
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allQueriesCompleted],
  );

  const scopeOptions = useMemo(
    () =>
      scopes.map((scope) => ({
        value: scope.sk,
        label: scope.content ?? "",
      })),
    [scopes],
  );

  useEffect(() => {
    setSelectedScopes(scopeOptions.map((o) => o.value));
  }, [scopeOptions]);

  const dropdownOptions = useMemo(() => {
    const matchingMetadatas =
      objectivesWithMetadata?.map((om) => om.metadata.fullMetadata) ?? [];

    return {
      understandings: sortBy(
        uniq(matchingMetadatas.map((m) => m.overarchingUnderstanding)),
      ),
      concepts: sortBy(
        uniq(matchingMetadatas.map((m) => m.overarchingConcept)),
      ),
      unitConcepts: sortBy(uniq(matchingMetadatas.map((m) => m.unitConcept))),
    };
  }, [objectivesWithMetadata]);

  const isLoading =
    objectivesQuery.isLoading || !!scopesQueries.find((q) => q.isLoading);

  const hasSelection =
    understanding != null ||
    concept != null ||
    unitConcept != null ||
    debouncedFilterText.length > 0;

  const { conceptualGroups, modalObjectives } = useMemo(() => {
    if (!hasSelection || isLoading)
      return { conceptualGroups: undefined, modalObjectives: undefined };

    const itemsByScope = groupBy(
      objectivesWithMetadata,
      (om) => om.objective.scope,
    );

    const modalObjectives: Objective[] = [];

    type ConceptualGroup = {
      key: string;
      label: string | undefined;
      groups: Array<{
        label: string;
        objectives: Array<{
          objective: Objective;
          metadata: UnselectedMetadata;
          index: number;
        }>;
      }>;
    };

    const conceptualGroups: ConceptualGroup[] = scopes
      .filter((s) => selectedScopes.includes(s.sk))
      .map(
        (scope) =>
          ({
            key: scope.sk,
            label: scope.content,
            groups: Object.entries(
              groupBy(
                itemsByScope[scope.sk.replace(/^section#/, "")],
                (om) => om.metadata.title ?? "",
              ),
            ).map(([title, objectives]) => ({
              label: title,
              objectives: (objectives as any).map((om: any) => {
                modalObjectives.push(om.objective);
                return {
                  index: modalObjectives.length - 1,
                  ...om,
                };
              }),
            })),
          }) as ConceptualGroup,
      )
      .reduce((prev, current) => {
        if (
          current.groups.length === 0 &&
          prev[prev.length - 1]?.groups.length === 0
        ) {
          prev[prev.length - 1].label += ", " + current.label;
        } else {
          prev.push(current);
        }
        return prev;
      }, [] as ConceptualGroup[]);

    return { conceptualGroups, modalObjectives };
  }, [hasSelection, isLoading, objectivesWithMetadata, scopes, selectedScopes]);

  return (
    <>
      <ObjectiveDetailModal
        objectives={modalObjectives}
        showIndex={showObjectiveIndex}
        onShowIndexChange={setShowObjectiveIndex}
      />
      <ContentSection
        title="TEKS Conceptual Alignment"
        actions={
          <Toolbar>
            {<Annotations resource={data} locationKey="CVAD" />}
          </Toolbar>
        }
      >
        <JumpBarContext.Provider
          value={{
            entries: jumpBarEntries,
            setEntries: setJumpBarEntries,
          }}
        >
          <StyledStickyToolbar>
            <StyledConceptSelection>
              <SelectInput
                options={dropdownOptions.understandings.map((o) => ({
                  label: o,
                  value: o,
                }))}
                placeholder="Overarching Understanding"
                value={understanding}
                onChange={(o) =>
                  setSelections({ ...selections, understanding: o?.value })
                }
                disabled={isLoading}
                isClearable
              />
              <SelectInput
                options={dropdownOptions.concepts.map((o) => ({
                  label: o,
                  value: o,
                }))}
                placeholder="Overarching Concept"
                value={concept}
                onChange={(o) =>
                  setSelections({ ...selections, concept: o?.value })
                }
                disabled={isLoading}
                isClearable
              />
              <SelectInput
                options={dropdownOptions.unitConcepts.map((o) => ({
                  label: o,
                  value: o,
                }))}
                placeholder="Unit / Module Concept"
                value={unitConcept}
                onChange={(o) =>
                  setSelections({ ...selections, unitConcept: o?.value })
                }
                disabled={isLoading}
                isClearable
              />
            </StyledConceptSelection>
            <Toolbar>
              <StyledJumpBar menuPosition="fixed" disabled={isLoading} />
              <StyledFilterTextInput
                placeholder="Filter by concepts/understandings or SE number/text"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                autoCapitalize="off"
                showClearButton={true}
                disabled={isLoading}
              />
              <CheckboxGridFilterButton
                options={scopeOptions}
                value={selectedScopes}
                onChange={setSelectedScopes}
                disabled={isLoading}
              />
            </Toolbar>
          </StyledStickyToolbar>
          <Styled>
            {conceptualGroups?.map((scope) =>
              scope.groups.length === 0 ? (
                <EmptySection key={scope.key}>
                  No matching standards for {scope.label}
                </EmptySection>
              ) : (
                <ContentSection key={scope.key} title={scope.label ?? ""}>
                  <StyledIndented>
                    {scope.groups.map((group) => (
                      <ContentSectionSecondary
                        key={group.label}
                        title={group.label}
                        header={
                          !!group.label && (
                            <>
                              <StyledButton
                                title="Search This Combination"
                                onClick={() => {
                                  const {
                                    overarchingUnderstanding,
                                    overarchingConcept,
                                    unitConcept,
                                  } = group.objectives[0].metadata.fullMetadata;
                                  setSelections({
                                    understanding: overarchingUnderstanding,
                                    concept: overarchingConcept,
                                    unitConcept: unitConcept,
                                  });
                                  setFilterText("");
                                }}
                              >
                                {group.label}
                              </StyledButton>
                            </>
                          )
                        }
                      >
                        <StyledIndented>
                          {group.objectives.map((om) => (
                            <StyledAlignmentViewObjective
                              key={om.objective.id}
                              objective={om.objective}
                              onObjectiveBadgeClick={() =>
                                setShowObjectiveIndex(om.index)
                              }
                            />
                          ))}
                        </StyledIndented>
                      </ContentSectionSecondary>
                    ))}
                  </StyledIndented>
                </ContentSection>
              ),
            )}
          </Styled>
        </JumpBarContext.Provider>
        {isLoading ? (
          <Loading>Loading Standards</Loading>
        ) : (
          conceptualGroups == null && (
            <NoResults icon={<IconInformationCircleLine />}>
              Select Overarching Understanding, Overarching Concept, Unit
              Concept, or filter text to list matching standards.
            </NoResults>
          )
        )}
      </ContentSection>
    </>
  );
};

export default ConceptualAlignment;
